import { useQuery } from '@tanstack/react-query';
import { compact, filter, find, get, includes, isEmpty, keyBy, map, values } from 'es-toolkit/compat';
import React from 'react';

import { getCancerTypes } from 'api/cancerTypes';
import { BiopsySiteType } from 'interfaces/biopsySiteType';
import { CancerSubtype, CancerType, CancerTypeMap } from 'interfaces/cancerType';
import { Procedure } from 'interfaces/procedure';
import { Slide, SlideQcLabel } from 'interfaces/slide';
import { SlideTag } from 'interfaces/slideTag';
import { Study } from 'interfaces/study';
import {
  EnumDisplayNames,
  useAllCancerTypes,
  useBiopsySiteTypes,
  useStainTypeOptions,
  useUiSettings,
} from 'utils/queryHooks/uiConstantsHooks';
import { useCurrentLabId } from 'utils/useCurrentLab';

import { StainType } from 'interfaces/stainType';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import useSlideTagOptions from 'utils/queryHooks/useSlideTagOptions';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { scannerModels } from './scannerModels';

export const getCancerSubTypes = (
  cancerTypeIds: number[],
  allCancerTypes: CancerTypeMap,
  cancerSubtypes: Record<string, CancerSubtype>
) => {
  const allCancerTypesList: CancerType[] = values(allCancerTypes);
  const cancerSubtypesList = get(cancerSubtypes, 'TISSUE')?.children;

  const cancerTypeObjects = filter(allCancerTypesList, (c) => includes(cancerTypeIds, c?.id));
  const mainCancerTypeCodes = map(cancerTypeObjects, (c) => c?.code?.toUpperCase());

  const relevantSubtypes: CancerSubtype[] = compact(map(mainCancerTypeCodes, (code) => cancerSubtypesList?.[code]));

  return { mainCancerTypeCodes, relevantSubtypes };
};

export const getProcedureSlideFieldValues = <T extends keyof Slide>(
  procedure: Procedure,
  field: T,
  getDefaultValue?: (slide: Slide) => any
): Array<Slide[T]> => {
  const fieldValues = map(procedure?.slides, (slide) => slide?.[field] ?? getDefaultValue?.(slide));
  return !isEmpty(filter(fieldValues, (v) => v !== undefined)) ? fieldValues : [];
};

export const getProcedureSlideQcLabelsValues = (procedure: Procedure, labelToCheck: string): Array<boolean | null> => {
  const slidesQcLabels: SlideQcLabel[][] = map(procedure?.slides, 'qcLabels');

  return map(slidesQcLabels, (slideQcLabels) => {
    const matchedLabel: SlideQcLabel = find(slideQcLabels, { text: labelToCheck });
    return matchedLabel ? (matchedLabel?.errorCalculatingLabel ? null : true) : false;
  });
};

export const getUpdatePathForProcedureClinicalDataField = <T extends keyof Procedure['clinicalData']>(
  field: T
): string => `clinicalData.${field}`;

export interface ProceduresFieldsContext {
  studies: Study[];
  studyById?: Record<string, Study>;
  cancerTypes?: CancerTypeMap;
  allCancerTypes?: CancerTypeMap;
  cancerSubtypes?: Record<string, CancerSubtype>;
  scannerModels?: string[];
  biopsySiteTypes?: BiopsySiteType[];
  stainTypesInStudy?: string[];
  slideTagOptions?: SlideTag[];
  slideTagOptionsWithArchived?: SlideTag[];
  enumDisplayNames?: EnumDisplayNames;
  stainTypes?: StainType[];
  stainTypesNotDeprecated?: StainType[];
  cancerTypesLoading?: boolean;
  allCancerTypesLoading?: boolean;
  stainTypesInStudyLoading?: boolean;
  stainTypesLoading?: boolean;
  biopsySiteTypesLoading?: boolean;
  studiesLoading?: boolean;
  slideTagOptionsLoading?: boolean;
  uiSettingsLoading?: boolean;
  isEditPreview?: boolean; // this is for cases that we show preview before bulk editing, like cases manifest
}

export const isProcedureFieldOptionsLoading = (context?: ProceduresFieldsContext) =>
  Boolean(
    context?.allCancerTypesLoading ||
      context?.uiSettingsLoading ||
      context?.stainTypesLoading ||
      context?.biopsySiteTypesLoading ||
      context?.studiesLoading ||
      context?.slideTagOptionsLoading
  );

export const useProceduresFieldsContext = ({
  enabled = true,
  isEditPreview = false,
}: {
  enabled?: boolean;
  isEditPreview?: boolean;
} = {}): ProceduresFieldsContext => {
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const studyId = queryParams?.filters?.studyId;

  const { data: cancerTypes, isLoading: cancerTypesLoading } = useQuery(
    ['cancerTypes', labId],
    ({ signal }) => getCancerTypes(labId, signal),
    { enabled }
  );

  const { allCancerTypes, isLoadingAllCancerTypes } = useAllCancerTypes({ enabled });
  const { data: stainTypesInStudy, isLoading: stainTypesInStudyLoading } = useStudyStainTypeIds(studyId, {
    enabled,
  });
  const { data: studies, isLoading: studiesLoading } = useStudies(labId, false, { enabled });
  const studyById = React.useMemo(() => keyBy(studies, 'id'), [studies]);
  const { slideTagOptions, slideTagOptionsWithArchived, isLoading: slideTagOptionsLoading } = useSlideTagOptions();

  const { uiSettings, isLoadingUiSettings } = useUiSettings();
  const { enumDisplayNames, cancerSubtypes } = uiSettings;
  const { biopsySiteTypes, isLoadingBiopsySiteTypes } = useBiopsySiteTypes();
  const { stainTypeOptions, isLoadingStainTypeOptions } = useStainTypeOptions();
  const stainTypesNotDeprecated = filter(stainTypeOptions, (stainType) => !stainType?.deletedAt);

  return {
    stainTypesInStudy,
    scannerModels,
    biopsySiteTypes,
    cancerTypes,
    allCancerTypes,
    enumDisplayNames,
    studyById,
    studies,
    slideTagOptions,
    slideTagOptionsWithArchived,
    stainTypes: stainTypeOptions,
    stainTypesNotDeprecated,
    cancerTypesLoading,
    allCancerTypesLoading: isLoadingAllCancerTypes,
    stainTypesInStudyLoading,
    stainTypesLoading: isLoadingStainTypeOptions,
    biopsySiteTypesLoading: isLoadingBiopsySiteTypes,
    studiesLoading,
    slideTagOptionsLoading,
    uiSettingsLoading: isLoadingUiSettings,
    cancerSubtypes,
    isEditPreview,
  };
};
